// Presentation shape for a fleet-wide row, derived from the projected sync row.
//
// This exists rather than reusing buildRowVm because the two rows are not the same
// thing. buildRowVm takes the fat IssueDto and counts signals by grouping
// `contributions`; the projected row has no contributions at all and carries
// signalCount already computed on the server. Feeding a sync row through buildRowVm
// would report every issue as having zero signals.
//
// Pure — no Angular, no services. Name and group-name resolution arrive as callbacks,
// the same way buildRowVm takes resolveName.

import { IssueDto, IssueLifecycleState } from '../models/issue';
import { ContributionSourceType, ContributionSourceTypes } from '../models/contribution';
import { IssueSyncRow } from '../sync/issue-sync-models';
import { SeverityCss, severityCss, severityTier } from 'src/app/service-center/severity';
import { confidencePct, initials, isConfidenceMuted, isUnsetDate, lifecycleCss } from '../issue-formatting';

// Where the asset is. A geofence that is not a shop leaves currentShopGroupId empty, so
// an asset at a customer site reads as on the road — a deliberate binary. 'unknown' is a
// tenant-level answer: the server only ever sets that id from a shop-flagged geofence,
// so a tenant configuring none gives us no basis to place any asset. We never guess.
export const IssueLocationStates = {
  Shop: 'shop', Road: 'road', Unknown: 'unknown',
} as const;
export type IssueLocationState = typeof IssueLocationStates[keyof typeof IssueLocationStates];

export interface IssueFleetRowVm {
  id: string;

  // Problem
  displayName: string;
  description: string;
  fingerprint: string;
  // The catalog's grouping for this problem — "Brakes", "Engine". Empty for an
  // uncatalogued fingerprint, which has no entry to take one from.
  category: string;
  severity: number;
  tierCss: SeverityCss;
  tier: number;
  confidencePct: number;
  confidenceMuted: boolean;
  lifecycleState: IssueLifecycleState;
  lifecycleCss: string;
  openedAtUtc: string;
  // The row's own last change. The per-asset row shows the last contribution firing,
  // which the projected row cannot know — it carries no contributions.
  lastActivityUtc: string;
  signalCount: number;
  reopened: boolean;
  origins: ContributionSourceType[];
  dvirOrigin: boolean;

  // Asset
  assetId: string;
  assetName: string;
  assetVin: string;
  make: string;
  model: string;
  modelYear: string;
  vehicleSeverity: number;
  vehicleTierCss: SeverityCss;

  // Where it is
  locationState: IssueLocationState;
  currentShopGroupId: string;
  currentShopName: string;
  homeLocationGroupIds: string[];
  // True when the asset sits at a shop that is not one of its home locations. Worth
  // showing rather than hiding: the scope said one place and the asset is in another.
  awayFromHome: boolean;
  lastMovedAtUtc: string;

  // Work
  caseId: string;
  hasCase: boolean;
  lastCaseActivityUtc: string;
  assignedUserId: string;
  assignedLocationId: string;
  assigneeInitials: string | null;
  assigneeName: string | null;
  unassigned: boolean;
}

export interface FleetRowContext {
  // Resolves a user id to a display name. Null when unknown.
  resolveUserName(userId: string): string | null;
  // Resolves a group id to its name. The row carries ids only — group names are
  // deliberately off the feed, so the caller supplies the reference list.
  resolveGroupName(groupId: string): string | null;
  // False when the tenant has no shop-flagged location groups. The server sets
  // currentShopGroupId only from one of those, so without any, an empty value carries no
  // information and every asset reads Unknown rather than being placed on the road.
  tenantHasShopLocations: boolean;
}

export function buildFleetRowVm(row: IssueSyncRow, ctx: FleetRowContext): IssueFleetRowVm {
  const atShop = !!row.currentShopGroupId;
  const locationState: IssueLocationState = atShop
    ? IssueLocationStates.Shop
    : (ctx.tenantHasShopLocations ? IssueLocationStates.Road : IssueLocationStates.Unknown);

  const assigneeName = row.assignedUserId ? ctx.resolveUserName(row.assignedUserId) : null;

  return {
    id: row.id,

    displayName: row.displayName,
    description: row.description ?? '',
    fingerprint: row.fingerprint,
    category: row.category,
    severity: row.severity,
    tierCss: severityCss(row.severity),
    tier: severityTier(row.severity),
    confidencePct: confidencePct(row.confidence),
    confidenceMuted: isConfidenceMuted(row.confidence),
    lifecycleState: row.lifecycleState,
    lifecycleCss: lifecycleCss(row.lifecycleState),
    openedAtUtc: row.openedAtUtc,
    lastActivityUtc: row.updateDateTimeUtc,
    signalCount: row.signalCount,
    reopened: row.reopenCount > 0,
    origins: row.origins ?? [],
    dvirOrigin: (row.origins ?? []).includes(ContributionSourceTypes.DVIRDefect),

    assetId: row.assetId,
    assetName: row.assetName,
    assetVin: row.assetVin,
    make: row.make,
    model: row.model,
    modelYear: row.modelYear,
    vehicleSeverity: row.vehicleSeverity,
    vehicleTierCss: severityCss(row.vehicleSeverity),

    locationState,
    currentShopGroupId: row.currentShopGroupId,
    currentShopName: atShop ? (ctx.resolveGroupName(row.currentShopGroupId) ?? '') : '',
    homeLocationGroupIds: row.homeLocationGroupIds ?? [],
    awayFromHome: atShop && !(row.homeLocationGroupIds ?? []).includes(row.currentShopGroupId),
    // The server stops maintaining this once an Issue closes, so a closed row would
    // state a frozen time as a current one.
    lastMovedAtUtc: isUnsetDate(row.lastMovedAtUtc) ? '' : row.lastMovedAtUtc,

    caseId: row.caseId,
    hasCase: !!row.caseId,
    lastCaseActivityUtc: isUnsetDate(row.lastCaseActivityUtc) ? '' : row.lastCaseActivityUtc,
    assignedUserId: row.assignedUserId,
    assignedLocationId: row.assignedLocationId,
    assigneeInitials: assigneeName ? initials(assigneeName) : null,
    assigneeName,
    unassigned: !row.assignedUserId,
  };
}

// A header-weight Issue built from the projected row, so a detail panel paints at once
// while the full document is read. The evidence, the activity and the case list are
// what the read is for; they start empty rather than wrong.
export function seedIssueFromRow(row: IssueSyncRow): IssueDto {
  return {
    id: row.id,
    createDateTimeUtc: row.openedAtUtc,
    updateDateTimeUtc: row.updateDateTimeUtc,
    assetId: row.assetId,
    assetName: row.assetName,
    assetVin: row.assetVin,
    fingerprint: row.fingerprint,
    category: row.category,
    confidence: row.confidence,
    severity: row.severity,
    lifecycleState: row.lifecycleState,
    canonicalName: row.displayName,
    displayName: row.displayName,
    description: row.description ?? '',
    contributions: [],
    stateHistory: [],
    activity: [],
    disposition: null,
    references: [],
    metricSnapshots: [],
    cases: [],
    reopenCount: row.reopenCount,
    lastReopenedAt: '',
  };
}
